import { useState } from "react";

import { styled } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";

import { CfLoader } from "@/shared/components/common/CfLoader/CfLoader";

import { CountrySelector } from "./CountrySelector";
import { EAgriAuthForm } from "./EAgriAuthForm";
import { EAgriFarmForm } from "./EAgriFarmForm";
import {
  CZECH_COUNTRY_CODE,
  IMPORT_TYPES,
  type ImportType,
} from "./farmImport.schema";
import { ImportTypeSelector } from "./ImportTypeSelector";
import { resolveInitialCountryCode } from "./initialCountry";
import { ManualFarmForm } from "./ManualFarmForm";
import { ShapefileFarmForm } from "./ShapefileFarmForm";
import { countriesQuery, eagriFarmNameQuery } from "./signup.api";

const FarmImport = (): React.ReactElement => {
  const { locale } = useIntl();
  const eagriFarmName = useQuery(eagriFarmNameQuery());
  const countries = useQuery(countriesQuery());
  const [pickedCountry, setPickedCountry] = useState<string | null>(null);
  const [pickedImportType, setPickedImportType] = useState<ImportType | null>(
    null,
  );

  const countryCode =
    pickedCountry ??
    resolveInitialCountryCode({
      locale,
      supportedCodes: (countries.data ?? []).map((country) => country.code),
    });
  const importType = pickedImportType ?? defaultImportType(countryCode);

  const handleCountryChange = (code: string) => {
    setPickedCountry(code);
    setPickedImportType(null);
  };

  if (eagriFarmName.isPending) {
    return <CfLoader />;
  }

  const content = eagriFarmName.isSuccess ? (
    <EAgriFarmForm eagriFarmName={eagriFarmName.data} />
  ) : (
    <>
      <CountrySelector onChange={handleCountryChange} value={countryCode} />
      <ImportSelection
        countryCode={countryCode}
        onImportTypeChange={setPickedImportType}
        value={importType}
      />
    </>
  );

  return (
    <div>
      <Heading>
        <FormattedMessage id="FarmImport.heading" />
      </Heading>
      <Description>
        <FormattedMessage id="FarmImport.desc" />
      </Description>
      <Layout>{content}</Layout>
    </div>
  );
};

const defaultImportType = (countryCode: string): ImportType =>
  countryCode === CZECH_COUNTRY_CODE
    ? IMPORT_TYPES.EAGRI
    : IMPORT_TYPES.SHAPEFILE;

type SelectionProps = {
  countryCode: string;
  value: ImportType;
  onImportTypeChange: (importType: ImportType) => void;
};

const ImportSelection = ({
  countryCode,
  onImportTypeChange,
  value,
}: SelectionProps): React.ReactElement | null => {
  if (!countryCode) {
    return null;
  }

  const forms: Record<ImportType, React.ReactElement> = {
    [IMPORT_TYPES.EAGRI]: <EAgriAuthForm countryCode={countryCode} />,
    [IMPORT_TYPES.MANUAL]: <ManualFarmForm countryCode={countryCode} />,
    [IMPORT_TYPES.SHAPEFILE]: <ShapefileFarmForm countryCode={countryCode} />,
  };

  return (
    <>
      <ImportTypeSelector
        countryCode={countryCode}
        onChange={onImportTypeChange}
        value={value}
      />
      {forms[value]}
    </>
  );
};

const Layout = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const Heading = styled("h5")({
  fontWeight: 500,
  fontSize: 24,
  marginTop: 16,
  marginBottom: 8,
  textAlign: "center",
});

const Description = styled("p")({
  fontSize: 16,
  textAlign: "center",
  marginBottom: 8,
});

export { FarmImport };
